import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  Checkbox,
  FormControlLabel,
  Icon,
  Input,
  LinearProgress,
  Radio,
  RadioGroup,
} from '@material-ui/core'
import { toast } from 'react-toastify'
import HttpHandler, { getModelChangeEvent } from 'app/reusable/HttpHandler'
import useRequest from 'app/hooks/useRequest'
import { useDismissModal } from 'app/ducks/modal/hooks'

const YesOrNoAnswer = ({ value, onChange }) => (
  <RadioGroup name="yes-or-no">
    <FormControlLabel
      control={<Radio checked={value === 'Sim'} />}
      label="Sim"
      onClick={() => onChange('Sim')}
    />
    <FormControlLabel
      control={<Radio checked={value === 'Não'} />}
      label="Não"
      onClick={() => onChange('Não')}
    />
  </RadioGroup>
)

const TextAnswer = ({ value, onChange }) => (
  <textarea
    rows={5}
    autoFocus
    className="w-full p-2"
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    style={{ border: '1px solid #e0e0e0', borderRadius: 4 }}
  />
)

const NumberAnswer = ({ value, onChange }) => (
  <Input
    type="number"
    autoFocus
    className="w-full"
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
  />
)

const SingleChoiceAnswer = ({ question, value, onChange }) => (
  <RadioGroup name="single-choice">
    {question.choices.map((choice) => (
      <div className="flex" key={choice.id}>
        <FormControlLabel
          control={<Radio checked={value?.choiceId === choice.id} />}
          label={choice.label}
          className="flex-grow"
          onClick={() =>
            onChange({
              choiceId: choice.id,
              label: choice.label,
              input: value?.input,
            })
          }
        />
        {choice.allow_input && value?.choiceId === choice.id && (
          <textarea
            rows={2}
            className="w-full p-2"
            value={value?.input || ''}
            onChange={(e) => onChange({ ...value, input: e.target.value })}
            style={{
              border: '1px solid #e0e0e0',
              borderRadius: 4,
              margin: '.5rem 0',
            }}
          />
        )}
      </div>
    ))}
  </RadioGroup>
)

const MultipleChoiceAnswer = ({ question, value, onChange }) => {
  const selected = value || {}

  return (
    <div>
      {question.choices.map((choice) => (
        <div className="flex" key={choice.id}>
          <FormControlLabel
            control={<Checkbox checked={!!selected[choice.id]} />}
            label={choice.label}
            className="flex-grow"
            onClick={() => {
              const next = { ...selected }
              if (next[choice.id]) delete next[choice.id]
              else next[choice.id] = { label: choice.label, input: null }
              onChange(next)
            }}
          />
          {choice.allow_input && selected[choice.id] && (
            <textarea
              rows={2}
              className="w-full p-2"
              value={selected[choice.id]?.input || ''}
              onChange={(e) =>
                onChange({
                  ...selected,
                  [choice.id]: {
                    ...selected[choice.id],
                    input: e.target.value,
                  },
                })
              }
              style={{
                border: '1px solid #e0e0e0',
                borderRadius: 4,
                margin: '.5rem 0',
              }}
            />
          )}
        </div>
      ))}
    </div>
  )
}

const QUESTION_ANSWER_COMPONENTS = {
  yes_or_no: YesOrNoAnswer,
  text: TextAnswer,
  number: NumberAnswer,
  single_choice: SingleChoiceAnswer,
  multiple_choice: MultipleChoiceAnswer,
}

const flattenAnswers = (questions, answers) => {
  const payload = []

  for (const question of questions) {
    const value = answers[question.id]
    if (value === undefined || value === null || value === '') continue

    if (question.type === 'single_choice') {
      payload.push({
        question: question.id,
        value: value.label,
        choice: value.choiceId,
        input: value.input || null,
      })
    } else if (question.type === 'multiple_choice') {
      Object.entries(value).forEach(([choiceId, choiceValue]) => {
        payload.push({
          question: question.id,
          value: choiceValue.label,
          choice: Number(choiceId),
          input: choiceValue.input || null,
        })
      })
    } else {
      payload.push({ question: question.id, value: String(value) })
    }
  }

  return payload
}

const ThankYouScreen = ({ onClose }) => (
  <div className="pt-6 text-center">
    <Icon style={{ fontSize: '4rem', color: '#4caf50' }}>check_circle</Icon>
    <h3 className="mt-3">Obrigado por responder!</h3>
    <p>Sua resposta foi registrada com sucesso.</p>
    <Button
      color="primary"
      variant="contained"
      size="large"
      className="w-full mt-4"
      onClick={onClose}
    >
      Fechar
    </Button>
  </div>
)

// Turns a saved response's answers back into this modal's per-question state
// (the inverse of `flattenAnswers`), so an existing response can be edited.
export const answersToState = (questions, answers) => {
  const typeByQuestion = Object.fromEntries(
    questions.map(({ id, type }) => [id, type])
  )
  return (answers || []).reduce((state, answer) => {
    const type = typeByQuestion[answer.question]
    if (type === 'single_choice') {
      state[answer.question] = {
        choiceId: answer.choice,
        label: answer.value,
        input: answer.input,
      }
    } else if (type === 'multiple_choice') {
      state[answer.question] = {
        ...(state[answer.question] || {}),
        [answer.choice]: { label: answer.value, input: answer.input },
      }
    } else if (type) {
      state[answer.question] = answer.value
    }
    return state
  }, {})
}

// Rows of one question's answer as the per-question save endpoints take
// them (without the `question` key), or [] when it's unanswered.
const questionRows = (question, answers) =>
  flattenAnswers([question], answers).map(({ question: _, ...row }) => row)

const firstUnansweredStep = (questions, answers) => {
  const index = questions.findIndex(
    (question) => !questionRows(question, answers).length
  )
  return index === -1 ? Math.max(questions.length - 1, 0) : index
}

const LOADERS = {
  self: ({ surveyId }) => HttpHandler.getSurveyToFill(surveyId),
  forUser: ({ surveyId }) => HttpHandler.getSurveyToFillForUser(surveyId),
  response: ({ responseId }) => HttpHandler.getSurveyResponseToEdit(responseId),
}

/**
 * Step-by-step survey filling, one question per screen.
 *
 * - `<SurveyFillModal surveyId={1} />`: the member's own self-fill (prompt).
 * - `<SurveyFillModal surveyId={1} user={{ id, name }} />`: filling for a user.
 * - `<SurveyFillModal responseId={7} />`: an existing response — resumed if
 *   still in progress, edited if already completed.
 *
 * While filling (every case but editing a completed response) each answer
 * is saved as soon as the user moves to another question or closes the
 * modal, so nothing is lost halfway; the response is only marked completed
 * on "Concluir". A staff fill creates its response with the first saved
 * answer. Editing a completed response keeps every change local and saves
 * them all at once on "Salvar".
 *
 * Staff modals close on finish, call `onSaved` and fire the
 * `survey-response` model-change event so open lists refresh.
 */
const SurveyFillModal = ({ surveyId, user, responseId, onSaved }) => {
  const dismissModal = useDismissModal()
  const source = responseId ? 'response' : user ? 'forUser' : 'self'
  const params = { surveyId, user, responseId }

  const surveyRequest = useRequest(LOADERS[source], `surveyToFill-${source}`, [
    params,
  ])
  const survey = surveyRequest.response?.data

  const [step, setStep] = useState(0)
  const [answers, setAnswers] = useState({})
  const [isSaving, setIsSaving] = useState(false)
  const [isFinished, setIsFinished] = useState(false)

  const questions = useMemo(() => survey?.questions || [], [survey])
  const currentQuestion = questions[step]
  const progress = questions.length
    ? Math.round((step / questions.length) * 100)
    : 0
  // Editing a completed response is the only case saved all at once.
  const isEditingCompleted = !!survey?.response?.completed_at
  const isIncremental = !!survey && !isEditingCompleted

  // What was last stored per question, the response being filled (a staff
  // fill only gets one with its first saved answer), and whether this modal
  // stored anything — kept in refs so the close handler sees current values.
  const savedRef = useRef({})
  const responseIdRef = useRef(responseId)
  const savedAnythingRef = useRef(false)
  const finishedRef = useRef(false)
  const latestRef = useRef({})
  latestRef.current = { answers, step, questions, isIncremental }

  useEffect(() => {
    if (!survey) return
    const loaded = answersToState(questions, survey.answers)
    savedRef.current = Object.fromEntries(
      questions.map((question) => [
        question.id,
        JSON.stringify(questionRows(question, loaded)),
      ])
    )
    setAnswers(loaded)
    if (!survey.response?.completed_at)
      setStep(firstUnansweredStep(questions, loaded))
  }, [survey, questions])

  // The survey may already be answered (e.g. filled in another tab); bail
  // out of the modal instead of getting stuck on the loading spinner.
  useEffect(() => {
    if (surveyRequest.error) dismissModal()
  }, [surveyRequest.error, dismissModal])

  const notifySaved = useCallback(() => {
    if (source === 'self') return
    window.dispatchEvent(getModelChangeEvent('surveys', 'survey-response'))
    onSaved?.()
  }, [source, onSaved])

  // Stores one question's answer if it changed since it was last stored.
  const saveQuestion = useCallback(
    async (question, currentAnswers) => {
      if (!question) return
      const rows = questionRows(question, currentAnswers)
      const serialized = JSON.stringify(rows)
      if (savedRef.current[question.id] === serialized) return

      if (source === 'self') {
        await HttpHandler.saveSelfFillAnswer(surveyId, question.id, rows)
      } else if (responseIdRef.current) {
        await HttpHandler.saveSurveyResponseAnswer(
          responseIdRef.current,
          question.id,
          rows
        )
      } else {
        if (!rows.length) return
        const { data } = await HttpHandler.startSurveyForUser(
          surveyId,
          user.id,
          question.id,
          rows
        )
        responseIdRef.current = data.pk
      }
      savedRef.current[question.id] = serialized
      savedAnythingRef.current = true
    },
    [source, surveyId, user]
  )

  // Closing the modal halfway keeps the current question's answer too.
  useEffect(
    () => () => {
      const { answers, step, questions, isIncremental } = latestRef.current
      if (!isIncremental || finishedRef.current) return
      saveQuestion(questions[step], answers)
        .then(() => savedAnythingRef.current && notifySaved())
        .catch(console.error)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  if (isFinished) {
    return <ThankYouScreen onClose={() => dismissModal()} />
  }

  if (surveyRequest.isLoading || !survey) {
    return (
      <div className="pt-6">
        <LinearProgress />
        <p className="text-center my-4">Carregando...</p>
      </div>
    )
  }

  const setAnswer = (value) => {
    setAnswers((prev) => ({ ...prev, [currentQuestion.id]: value }))
  }

  const goTo = async (nextStep) => {
    if (!isIncremental) return setStep(nextStep)
    setIsSaving(true)
    try {
      await saveQuestion(currentQuestion, answers)
      setStep(nextStep)
    } catch (error) {
      console.error(error)
      toast.error('Não foi possível salvar a resposta. Tente novamente.')
    } finally {
      setIsSaving(false)
    }
  }

  const finish = () => {
    if (isEditingCompleted) {
      return HttpHandler.updateSurveyResponseAnswers(
        responseIdRef.current,
        flattenAnswers(questions, answers)
      )
    }
    return saveQuestion(currentQuestion, answers).then(() =>
      source === 'self'
        ? HttpHandler.completeSelfFill(surveyId)
        : HttpHandler.completeSurveyResponse(responseIdRef.current)
    )
  }

  const handleFinish = () => {
    if (!flattenAnswers(questions, answers).length) {
      toast.warn('Responda ao menos uma pergunta.')
      return
    }

    setIsSaving(true)
    toast
      .promise(finish(), {
        pending: 'Enviando...',
        success: isEditingCompleted
          ? 'Respostas atualizadas!'
          : 'Respostas enviadas com sucesso!',
        error: 'Erro ao enviar respostas.',
      })
      .then(() => {
        finishedRef.current = true
        if (source === 'self') {
          setIsFinished(true)
          return
        }
        notifySaved()
        dismissModal()
      })
      .catch(console.error)
      .finally(() => setIsSaving(false))
  }

  const AnswerComponent = QUESTION_ANSWER_COMPONENTS[currentQuestion?.type]
  const subjectName = user?.name || survey.response?.user

  // `pt-6`: the modal has no title, so SimpleModal's negative top margin
  // would otherwise leave the content touching the dialog's top edge.
  return (
    <div className="pt-6">
      {source !== 'self' && (
        <div className="mb-4 t-survey-fill-header">
          <h3 className="m-0">{survey.name}</h3>
          {subjectName && (
            <p className="text-muted m-0 mt-1">
              <b>Para:</b> {subjectName}
            </p>
          )}
        </div>
      )}
      <LinearProgress variant="determinate" value={progress} className="mb-4" />
      <p className="text-muted mb-2">
        Pergunta {step + 1} de {questions.length}
      </p>
      <h4 className="mb-4 t-survey-fill-question">{currentQuestion?.title}</h4>

      {AnswerComponent && (
        <AnswerComponent
          key={currentQuestion.id}
          question={currentQuestion}
          value={answers[currentQuestion.id]}
          onChange={setAnswer}
        />
      )}

      <div className="flex flex-between flex-center-v mt-6">
        <Button
          disabled={step === 0 || isSaving}
          onClick={() => goTo(step - 1)}
        >
          Voltar
        </Button>
        {isSaving && (
          <span className="text-muted t-survey-fill-saving">Salvando...</span>
        )}
        {step < questions.length - 1 ? (
          <Button
            color="primary"
            variant="contained"
            disabled={isSaving}
            onClick={() => goTo(step + 1)}
          >
            Avançar
          </Button>
        ) : (
          <Button
            color="primary"
            variant="contained"
            disabled={isSaving}
            onClick={handleFinish}
          >
            {isEditingCompleted ? 'Salvar' : 'Concluir'}
          </Button>
        )}
      </div>
    </div>
  )
}

export default SurveyFillModal
